import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ReceiptIcon as Receipt, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowSquareOutIcon as ArrowSquareOut, CurrencyDollarIcon as CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { IntlSellerAgreementDialog, isIntlAgreementError } from "@/components/IntlSellerAgreementDialog";
import PayoutProgressMeter from "@/components/PayoutProgressMeter";

export default function Payouts() {
  const [account, setAccount] = useState(null);
  const [onboarded, setOnboarded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setupError, setSetupError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/connect/account");
      setAccount(r.data?.account || null);
      setOnboarded(Boolean(r.data?.onboarded));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const [intlOpen, setIntlOpen] = useState(false);

  const startOnboarding = async () => {
    setBusy(true);
    setSetupError(null);
    try {
      const r = await api.post("/connect/account", {
        return_url: webOrigin() + "/connect/return?flow=breeder",
      });
      window.location.href = r.data.url;
    } catch (err) {
      if (isIntlAgreementError(err)) {
        setIntlOpen(true);
        setBusy(false);
        return;
      }
      const detail = err?.response?.data?.detail;
      // Structured platform-setup errors render as a styled panel
      // (with "Open Stripe" fix link) rather than a tiny toast.
      if (detail && typeof detail === "object" && detail.message) {
        setSetupError(detail);
      } else {
        toast.error(formatApiError(err));
      }
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/connect/dashboard-link");
      window.open(r.data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Money</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Payouts</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Connect your bank account through Stripe to receive payments from buyers when you sell a dog or puppy through the marketplace. We use Stripe's industry-standard escrow flow to protect both parties.
        </p>
      </div>

      {setupError && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 max-w-2xl mb-6" data-testid="stripe-setup-error">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">We can&rsquo;t create your payouts account yet</p>
              <p className="text-[13px] text-amber-900 mt-1 leading-relaxed">{setupError.message}</p>
              {setupError.fix_url && (
                <a
                  href={setupError.fix_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-ink hover:bg-crimson text-white text-[12.5px] font-semibold px-4 py-2 transition-colors"
                  data-testid="stripe-fix-link"
                >
                  Open Stripe to fix this <ArrowSquareOut size={12} />
                </a>
              )}
              <p className="text-[11px] text-amber-800 mt-3">
                After completing the setup in Stripe, come back here and try again.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : !account ? (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 max-w-2xl">
          <div className="flex items-start gap-4 mb-5">
            <div className="p-3 rounded-xl bg-[#FBF7EC] border border-[#E8D9A6]">
              <Receipt size={28} weight="duotone" className="text-gold" />
            </div>
            <div>
              <h2 className="font-spectral text-2xl tracking-tight">Get paid for marketplace sales</h2>
              <p className="text-sm text-[#6B6B6B] mt-1">
                One-time setup with Stripe Express (~ 5 minutes). Stripe handles tax docs, KYC, and direct deposits to your bank.
              </p>
            </div>
          </div>
          <ul className="space-y-2.5 text-sm text-ink mb-6">
            <li className="flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-ink mt-0.5 flex-shrink-0" />
              5% platform fee on each sale — no monthly fees.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-ink mt-0.5 flex-shrink-0" />
              Funds held in escrow until the buyer confirms pickup.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-ink mt-0.5 flex-shrink-0" />
              Auto-releases after 14 days if no dispute.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-ink mt-0.5 flex-shrink-0" />
              Direct deposit to your bank, typically within 1–3 business days after release.
            </li>
          </ul>
          <Button
            onClick={startOnboarding}
            disabled={busy}
            data-testid="payouts-start-onboarding-btn"
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
          >
            {busy ? "Opening Stripe…" : (
              <>
                <CurrencyDollar size={16} className="mr-1.5" weight="bold" />
                Set up payouts with Stripe
              </>
            )}
          </Button>
          <p className="mt-3 text-xs text-[#6B6B6B] leading-snug" data-testid="payouts-preflight-hint">
            Takes about 5 minutes. Have handy: a photo ID, your bank routing
            &amp; account numbers, and your SSN (or EIN for a business).
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 max-w-2xl" data-testid="payouts-account-card">
          <div className="flex items-start gap-4 mb-5">
            <div className={`p-3 rounded-xl ${onboarded ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"}`}>
              {onboarded ? (
                <CheckCircle size={28} weight="fill" className="text-green-600" />
              ) : (
                <Warning size={28} weight="duotone" className="text-yellow-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-spectral text-2xl tracking-tight" data-testid="payouts-status">
                {onboarded ? "Payouts active" : "Setup incomplete"}
              </h2>
              <p className="text-sm text-[#6B6B6B] mt-1">
                {onboarded
                  ? "You can receive payments from marketplace buyers."
                  : "Stripe needs a bit more info before you can accept payments."}
              </p>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm mb-6">
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
              <dt className="text-xs uppercase tracking-wider text-[#6B6B6B]">Charges enabled</dt>
              <dd className="font-medium mt-0.5">{account.charges_enabled ? "Yes" : "No"}</dd>
            </div>
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
              <dt className="text-xs uppercase tracking-wider text-[#6B6B6B]">Payouts enabled</dt>
              <dd className="font-medium mt-0.5">{account.payouts_enabled ? "Yes" : "No"}</dd>
            </div>
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
              <dt className="text-xs uppercase tracking-wider text-[#6B6B6B]">Country</dt>
              <dd className="font-medium mt-0.5">{(account.country || "—").toUpperCase()}</dd>
            </div>
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
              <dt className="text-xs uppercase tracking-wider text-[#6B6B6B]">Currency</dt>
              <dd className="font-medium mt-0.5">{(account.default_currency || "—").toUpperCase()}</dd>
            </div>
          </dl>

          <PayoutProgressMeter progress={account.setup_progress} />

          {!!(account.requirements_currently_due || []).length && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-5">
              <p className="font-medium text-sm text-yellow-900 mb-2">Stripe still needs:</p>
              <ul className="text-xs text-yellow-800 space-y-0.5">
                {(account.requirements_currently_due_labels || account.requirements_currently_due || []).map((r) => (
                  <li key={r}>• {r}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!onboarded && (
              <Button onClick={startOnboarding} disabled={busy} data-testid="payouts-resume-btn"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
                {busy ? "Opening…" : "Finish setup"}
              </Button>
            )}
            {onboarded && (
              <Button onClick={openDashboard} disabled={busy} data-testid="payouts-dashboard-btn"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
                <ArrowSquareOut size={14} className="mr-1.5" />
                Open Stripe dashboard
              </Button>
            )}
            <Button variant="outline" onClick={refresh} className="rounded-full border-[#E5E5E5]" data-testid="payouts-refresh-btn">
              Refresh status
            </Button>
          </div>
        </div>
      )}

      {/* Tax / 1099-K notice — applies to anyone earning via the platform */}
      <div className="mt-8 max-w-2xl bg-[#FBF7EC] border border-[#E8D9A6] rounded-xl p-5" data-testid="payouts-tax-notice">
        <h3 className="font-spectral text-lg text-[#8A6A00] mb-2">You are the merchant of record</h3>
        <p className="text-[12px] text-[#3F3F3F] mb-3 leading-relaxed">
          Armstrong K9 Registry is a venue that connects breeders
          with buyers and routes payments through Stripe Connect. <strong>You</strong>{" "}
          are the seller of every dog you list, the merchant of record for every
          transaction, and responsible for all tax obligations arising from your
          sales. The platform does not collect or remit sales tax, file 1099 forms
          on your behalf, or act as a tax intermediary.
        </p>
        <ul className="text-[13px] text-[#3F3F3F] space-y-1.5 leading-relaxed list-disc pl-5">
          <li><strong>1099-K</strong> is issued by <strong>Stripe directly to you</strong> when your annual payouts cross the IRS threshold ($600 in 2026 for US sellers). The platform is not part of that filing.</li>
          <li><strong>Sales tax</strong> in your nexus state is your responsibility. We do not calculate, collect, or remit it.</li>
          <li><strong>Income tax</strong> on your earnings is your responsibility. We recommend setting aside ~25–30% of net payouts.</li>
          <li><strong>KYC and W-9 collection</strong> are handled by Stripe during Connect onboarding. Have your SSN or EIN ready.</li>
          <li><strong>OFAC-sanctioned countries</strong> can't complete onboarding — Stripe enforces this automatically.</li>
        </ul>
        <p className="text-[11px] text-[#6B6B6B] mt-3 leading-relaxed">
          This isn't tax advice. If you sell more than a handful of dogs per year
          or operate across state lines, please consult a CPA.
        </p>
      </div>
      <IntlSellerAgreementDialog open={intlOpen} onOpenChange={setIntlOpen} onAccepted={startOnboarding} />
    </AppShell>
  );
}
